import { useMemo, useState } from 'react';

interface TriviaQuestion {
  id: string;
  question: string;
  answer: string;
  explainer?: string;
}

interface Props {
  questions: TriviaQuestion[];
  send: (msg: any) => void;
}

export default function AdminTrivia({ questions, send }: Props) {
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQ, setEditQ] = useState('');
  const [editA, setEditA] = useState('');
  const [editE, setEditE] = useState('');
  const [newQ, setNewQ] = useState('');
  const [newA, setNewA] = useState('');
  const [newE, setNewE] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return questions;
    return questions.filter(q =>
      q.id.toLowerCase().includes(f) ||
      q.question.toLowerCase().includes(f) ||
      q.answer.toLowerCase().includes(f) ||
      (q.explainer || '').toLowerCase().includes(f)
    );
  }, [questions, filter]);

  function startEdit(q: TriviaQuestion) {
    setEditingId(q.id);
    setEditQ(q.question);
    setEditA(q.answer);
    setEditE(q.explainer ?? '');
  }

  function commitEdit() {
    if (!editingId || !editQ.trim() || !editA.trim()) return;
    send({ type: 'TRIVIA_EDIT', id: editingId, question: editQ.trim(), answer: editA.trim(), explainer: editE.trim() });
    setEditingId(null);
  }

  function commitAdd() {
    if (!newQ.trim() || !newA.trim()) return;
    send({ type: 'TRIVIA_ADD', question: newQ.trim(), answer: newA.trim(), explainer: newE.trim() });
    setNewQ('');
    setNewA('');
    setNewE('');
  }

  function commitDelete(id: string) {
    if (deleteConfirmId !== id) {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(curr => curr === id ? null : curr), 3000);
      return;
    }
    setDeleteConfirmId(null);
    send({ type: 'TRIVIA_DELETE', id });
  }

  return (
    <div className="adm-trivia">
      <div className="adm-toolbar">
        <input
          className="adm-input adm-search"
          type="text"
          placeholder={`Search ${questions.length} questions…`}
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <a className="adm-link" href="/party" target="_blank" rel="noreferrer">
          ▶ Open /party display
        </a>
      </div>

      <div className="adm-trivia-add">
        <div className="adm-section-label">+ Add Question</div>
        <textarea
          className="adm-input"
          rows={2}
          placeholder="Question"
          value={newQ}
          onChange={e => setNewQ(e.target.value)}
        />
        <textarea
          className="adm-input"
          rows={2}
          placeholder="Answer"
          value={newA}
          onChange={e => setNewA(e.target.value)}
        />
        <textarea
          className="adm-input"
          rows={2}
          placeholder="Explainer (optional, one sentence shown with the answer)"
          value={newE}
          onChange={e => setNewE(e.target.value)}
        />
        <button
          className="adm-btn adm-btn-primary"
          onClick={commitAdd}
          disabled={!newQ.trim() || !newA.trim()}
        >
          Add to Database
        </button>
      </div>

      <div className="adm-trivia-list">
        {filtered.map(q => {
          const isEditing = editingId === q.id;
          return (
            <div key={q.id} className="adm-trivia-row">
              {isEditing ? (
                <>
                  <textarea
                    className="adm-input"
                    rows={2}
                    value={editQ}
                    onChange={e => setEditQ(e.target.value)}
                  />
                  <textarea
                    className="adm-input"
                    rows={2}
                    value={editA}
                    onChange={e => setEditA(e.target.value)}
                  />
                  <textarea
                    className="adm-input"
                    rows={2}
                    value={editE}
                    onChange={e => setEditE(e.target.value)}
                    placeholder="Explainer (optional)"
                  />
                  <div className="adm-row-actions">
                    <button className="adm-btn adm-btn-primary" onClick={commitEdit}>Save</button>
                    <button className="adm-btn" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="adm-trivia-q">
                    <span className="adm-trivia-id">{q.id}</span>
                    {q.question}
                  </div>
                  <div className="adm-trivia-a">→ {q.answer}</div>
                  {q.explainer && (
                    <div className="adm-trivia-e">{q.explainer}</div>
                  )}
                  <div className="adm-row-actions">
                    <button className="adm-btn" onClick={() => startEdit(q)}>Edit</button>
                    <button
                      className={`adm-btn adm-btn-danger ${deleteConfirmId === q.id ? 'adm-btn-confirming' : ''}`}
                      onClick={() => commitDelete(q.id)}
                    >
                      {deleteConfirmId === q.id ? 'Confirm delete?' : 'Delete'}
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="adm-empty">No questions match.</div>
        )}
      </div>
    </div>
  );
}
